// Pure geometry + reading of a metric's series: the sparkline path, its points,
// the x-axis ticks, the target reference line, and whether the latest reading is
// on the right side of that target.
//
// Everything is expressed in PERCENTAGES of the plot box, not pixels. The chart
// stretches to the card's width (preserveAspectRatio="none"), which would render
// an SVG <circle> as an ellipse — so the marks are positioned in percent and drawn
// as HTML, while the SVG carries only the path and the target line.

import type { MetricInput } from "@/types";
import type { MetricView, PointRecord } from "./dashboard";

/** Readings older than this are dropped: a sparkline is a recent trend, not history. */
export const MAX_POINTS = 12;
/** Above this many readings, horizontal date labels collide and must be angled. */
export const DENSE_AXIS = 6;
/** Keeps the stroke and the point markers off the top and bottom edges. */
const PAD_PCT = 12;
const FULL_PCT = 100;

export type Marker = {
  /** Position within the plot box, 0–100. */
  xPct: number;
  yPct: number;
  value: number;
  at: string;
};

export type Tick = { label: string; xPct: number };

export type Sparkline = {
  /** SVG path in a 0 0 100 100 viewBox, stretched to the plot box. */
  path: string;
  markers: Marker[];
  /** The target's y in the plot box, or null when unset. */
  targetYPct: number | null;
  ticks: Tick[];
};

export type Standing = "onTarget" | "offTarget" | "noTarget";

/** Which way is "up" for a metric — the contract's `MetricDirection`. */
type Direction = MetricInput["direction"];

/** Whether the latest reading meets its target, respecting the metric's direction. */
export function standing(metric: MetricInput, latest: number | undefined): Standing {
  if (metric.target === undefined || latest === undefined) return "noTarget";
  const meets =
    metric.direction === "higherIsBetter" ? latest >= metric.target : latest <= metric.target;
  return meets ? "onTarget" : "offTarget";
}

/** The trailing readings, oldest first. */
export function recentPoints({ points }: MetricView): PointRecord[] {
  return points.slice(-MAX_POINTS);
}

/**
 * Map the readings onto the plot box. The y-scale spans the readings *and* the
 * target, so the target line is always visible and the series is read against it.
 * Null when there is nothing to draw — a line needs two points.
 */
export function sparkline(
  points: PointRecord[],
  target: number | undefined,
  direction: Direction,
): Sparkline | null {
  if (points.length < 2) return null;

  const values = points.map((point) => point.fields.value);
  const toY = yScale(values, target, direction);
  const stepX = FULL_PCT / (points.length - 1);

  const markers: Marker[] = points.map((point, index) => ({
    xPct: round(index * stepX),
    yPct: round(toY(point.fields.value)),
    value: point.fields.value,
    at: point.fields.at,
  }));

  const path = markers
    .map((marker, index) => `${index === 0 ? "M" : "L"}${marker.xPct} ${marker.yPct}`)
    .join(" ");

  return {
    path,
    markers,
    targetYPct: target === undefined ? null : round(toY(target)),
    ticks: axisTicks(markers),
  };
}

/** Every reading is dated on the axis; a dense series angles its labels instead
 * of dropping any (see DENSE_AXIS). */
function axisTicks(markers: Marker[]): Tick[] {
  return markers.map((marker) => ({ label: axisDate(marker.at), xPct: marker.xPct }));
}

/** Short axis date, e.g. "Jul 5" — the year is carried by the card, not the axis. */
function axisDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Value → y%, inset by the padding. The axis is oriented by the metric's direction
 * so that *better always climbs*: on a `lowerIsBetter` metric (pace, spend) the
 * scale is flipped, and a falling series — an improving one — draws an upward line,
 * exactly as a rising series does on a `higherIsBetter` metric. Without this the
 * same good news reads as decline on one card and progress on the next.
 */
function yScale(
  values: number[],
  target: number | undefined,
  direction: Direction,
): (value: number) => number {
  const all = target === undefined ? values : [...values, target];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min;
  const usable = FULL_PCT - PAD_PCT * 2;

  // A flat series has no span to scale against — draw it down the middle.
  if (span === 0) return () => FULL_PCT / 2;

  return (value) => {
    const fraction = (value - min) / span;
    const worseToBetter = direction === "higherIsBetter" ? 1 - fraction : fraction;
    return PAD_PCT + worseToBetter * usable;
  };
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
